import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  CloudArrowUp, DownloadSimple, ArrowClockwise, ShieldWarning, CheckCircle,
  XCircle, Clock, Info,
} from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RESTORE_CMD = `systemctl stop ato-backend
mongorestore --uri="mongodb://localhost:27017" --archive="/var/backups/ato-mongo/NOM.gz" --gzip --drop
systemctl start ato-backend`;

const humanBytes = (n) => {
  if (n == null) return '—';
  const k = 1024;
  const u = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0;
  while (n >= k && i < u.length - 1) { n /= k; i += 1; }
  return `${n.toFixed(n < 10 && i > 0 ? 1 : 0)} ${u[i]}`;
};

const humanDate = (iso, locale) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(locale || 'fr-FR', {
      year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

const StatusPill = ({ ok }) => (
  ok === true ? (
    <span className="inline-flex items-center gap-1 text-success text-xs font-medium bg-success-soft border border-success/30 rounded-full px-2 py-0.5">
      <CheckCircle size={12} weight="fill" /> OK
    </span>
  ) : ok === false ? (
    <span className="inline-flex items-center gap-1 text-danger text-xs font-medium bg-danger-soft border border-danger/30 rounded-full px-2 py-0.5">
      <XCircle size={12} weight="fill" /> Échec
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 text-text_muted text-xs font-medium bg-surface_muted border border-border rounded-full px-2 py-0.5">
      <Clock size={12} /> —
    </span>
  )
);

export const BackupsSection = () => {
  const [status, setStatus] = useState(null);
  const [local, setLocal] = useState({ available: false, files: [] });
  const [r2, setR2] = useState({ available: false, files: [] });
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const [s, l, r] = await Promise.all([
        axios.get(`${API}/admin/backups/status`, { withCredentials: true }),
        axios.get(`${API}/admin/backups/local`, { withCredentials: true }),
        axios.get(`${API}/admin/backups/r2`, { withCredentials: true }),
      ]);
      setStatus(s.data);
      setLocal(l.data);
      setR2(r.data);
    } catch (e) {
      if (e.response?.status === 403) {
        toast.error("Accès refusé — cette section est réservée au super-admin.");
      } else {
        toast.error("Impossible de charger l'état des sauvegardes");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Auto-refresh every 10s while a backup is running so the UI reflects
  // completion in near-real-time without a full page reload.
  useEffect(() => {
    if (!status?.in_progress && !running) return;
    const id = setInterval(fetchAll, 10000);
    return () => clearInterval(id);
  }, [status?.in_progress, running, fetchAll]);

  const runManual = async () => {
    if (running || status?.in_progress) return;
    setRunning(true);
    try {
      const { data } = await axios.post(`${API}/admin/backups/run`, {}, { withCredentials: true });
      if (data.queued) {
        toast.success('Sauvegarde manuelle lancée — elle s\'exécute en arrière-plan');
      } else if (data.in_progress) {
        toast.info('Une sauvegarde est déjà en cours');
      }
      // Give the child process a beat to write the lock, then refresh.
      setTimeout(fetchAll, 1500);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Impossible de lancer la sauvegarde');
    } finally {
      setRunning(false);
    }
  };

  const download = (name) => {
    // Download via the browser's cookie session — the endpoint validates
    // the filename against a strict regex before streaming.
    const url = `${API}/admin/backups/download?name=${encodeURIComponent(name)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const r2Names = new Set((r2.files || []).map(f => f.name));

  if (loading) {
    return (
      <div className="flex justify-center py-20" data-testid="backups-loading">
        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const canBackup = status?.available && !status?.in_progress && !running;

  return (
    <div className="space-y-6" data-testid="backups-section">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold flex items-center gap-2">
            <CloudArrowUp size={22} className="text-primary" /> Backups
          </h1>
          <p className="text-sm text-text_muted mt-1">
            Section réservée au super-admin — lecture, monitoring et téléchargement uniquement.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={fetchAll}
          data-testid="backups-refresh-btn"
        >
          <ArrowClockwise size={14} className="mr-1.5" />
          Actualiser
        </Button>
      </div>

      {!status?.available && (
        <div className="bg-warning/10 border border-warning/40 rounded-xl p-4 text-sm" data-testid="backups-not-configured">
          <div className="flex items-start gap-3">
            <ShieldWarning size={18} className="text-warning shrink-0 mt-0.5" weight="fill" />
            <div>
              <p className="font-medium text-warning mb-1">Infrastructure de sauvegarde non configurée</p>
              <p className="text-text_main text-[13px] leading-relaxed">
                Le dossier <code className="bg-surface_muted px-1 rounded">/var/backups/ato-mongo/</code> ou
                le script <code className="bg-surface_muted px-1 rounded">/opt/ato-scripts/ato-backup.sh</code>
                {' '}est introuvable sur cet environnement. Cette section est destinée au serveur de production.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Status cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="backups-status-cards">
        <div className="bg-white border border-border rounded-xl p-4">
          <p className="text-xs text-text_muted uppercase tracking-wide">Dernière sauvegarde MongoDB</p>
          <p className="text-lg font-semibold mt-1" data-testid="latest-mongo-date">
            {humanDate(status?.latest_mongo?.mtime)}
          </p>
          <div className="flex items-center gap-2 mt-1.5">
            <StatusPill ok={status?.mongo_success} />
            <span className="text-xs text-text_muted">
              {humanBytes(status?.latest_mongo?.size)}
            </span>
            {status?.latest_mongo?.type && (
              <Badge variant="outline" className="text-[10px] capitalize">
                {status.latest_mongo.type}
              </Badge>
            )}
          </div>
        </div>

        <div className="bg-white border border-border rounded-xl p-4">
          <p className="text-xs text-text_muted uppercase tracking-wide">Rétention</p>
          <p className="text-lg font-semibold mt-1" data-testid="retention-info">
            {status?.retention_days ?? 30} jours
          </p>
          <p className="text-xs text-text_muted mt-1.5">
            {status?.local_count ?? 0} sauvegarde{(status?.local_count ?? 0) > 1 ? 's' : ''} locale{(status?.local_count ?? 0) > 1 ? 's' : ''}
          </p>
        </div>

        <div className="bg-white border border-border rounded-xl p-4">
          <p className="text-xs text-text_muted uppercase tracking-wide">Images (synchronisation R2)</p>
          <div className="flex items-center gap-2 mt-1.5" data-testid="images-status">
            <StatusPill ok={status?.images_success} />
            <span className="text-xs text-text_muted">
              via <code className="bg-surface_muted px-1 rounded">/var/log/ato-images-backup.log</code>
            </span>
          </div>
        </div>
      </div>

      {/* Manual backup trigger */}
      <div className="bg-white border border-border rounded-xl p-4 flex items-center gap-3" data-testid="manual-run-card">
        <div className="flex-1">
          <p className="font-medium">Lancer une sauvegarde maintenant</p>
          <p className="text-xs text-text_muted mt-0.5">
            Déclenche <code className="bg-surface_muted px-1 rounded">/opt/ato-scripts/ato-backup.sh manual</code>.
            Argument codé en dur côté serveur, exécution asynchrone, verrou anti-concurrence actif.
          </p>
        </div>
        <Button
          onClick={runManual}
          disabled={!canBackup}
          className="bg-primary text-white gap-2 rounded-full"
          data-testid="manual-run-btn"
        >
          {(status?.in_progress || running) ? (
            <>
              <ArrowClockwise size={14} className="animate-spin" />
              Sauvegarde en cours…
            </>
          ) : (
            <>
              <CloudArrowUp size={14} weight="bold" />
              Lancer maintenant
            </>
          )}
        </Button>
      </div>

      {/* Local backups table */}
      <div className="bg-white border border-border rounded-xl overflow-hidden" data-testid="local-backups-table">
        <div className="px-4 py-3 border-b border-border flex items-center justify-between">
          <div>
            <p className="font-medium">Sauvegardes locales</p>
            <p className="text-xs text-text_muted">
              Dossier <code className="bg-surface_muted px-1 rounded">{status?.backup_dir}</code>
            </p>
          </div>
          <Badge variant="outline">{local.files?.length || 0}</Badge>
        </div>
        {(local.files?.length || 0) === 0 ? (
          <p className="text-center text-text_muted py-8 text-sm">Aucune sauvegarde locale.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-surface_muted text-xs text-text_muted uppercase tracking-wide">
              <tr>
                <th className="text-left px-4 py-2">Date</th>
                <th className="text-left px-4 py-2">Type</th>
                <th className="text-left px-4 py-2">Taille</th>
                <th className="text-left px-4 py-2">R2</th>
                <th className="text-right px-4 py-2">Action</th>
              </tr>
            </thead>
            <tbody>
              {local.files.map(f => (
                <tr key={f.name} className="border-t border-border" data-testid={`local-row-${f.name}`}>
                  <td className="px-4 py-2 font-mono text-xs">
                    {humanDate(f.mtime)}
                    <div className="text-[11px] text-text_muted">{f.name}</div>
                  </td>
                  <td className="px-4 py-2">
                    <Badge variant="outline" className="capitalize">{f.type}</Badge>
                  </td>
                  <td className="px-4 py-2">{humanBytes(f.size)}</td>
                  <td className="px-4 py-2">
                    {r2Names.has(f.name) ? (
                      <span className="inline-flex items-center gap-1 text-success text-xs">
                        <CheckCircle size={13} weight="fill" /> synchronisé
                      </span>
                    ) : (
                      <span className="text-xs text-text_muted">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => download(f.name)}
                      data-testid={`download-btn-${f.name}`}
                      className="gap-1.5"
                    >
                      <DownloadSimple size={13} />
                      Télécharger
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* R2 backups table (read-only, no download) */}
      <div className="bg-white border border-border rounded-xl overflow-hidden" data-testid="r2-backups-table">
        <div className="px-4 py-3 border-b border-border flex items-center justify-between">
          <div>
            <p className="font-medium">Sauvegardes Cloudflare R2</p>
            <p className="text-xs text-text_muted">
              Bucket <code className="bg-surface_muted px-1 rounded">atomadagascar/mongo/</code>
              {' '}— lecture seule, téléchargement uniquement depuis le local
            </p>
          </div>
          <Badge variant="outline">{r2.files?.length || 0}</Badge>
        </div>
        {!r2.available ? (
          <p className="text-center text-text_muted py-8 text-sm">
            R2 injoignable depuis cet environnement ({r2.reason || 'rclone indisponible'}).
          </p>
        ) : (r2.files?.length || 0) === 0 ? (
          <p className="text-center text-text_muted py-8 text-sm">Aucune sauvegarde sur R2.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-surface_muted text-xs text-text_muted uppercase tracking-wide">
              <tr>
                <th className="text-left px-4 py-2">Date</th>
                <th className="text-left px-4 py-2">Type</th>
                <th className="text-left px-4 py-2">Taille</th>
                <th className="text-left px-4 py-2">Statut</th>
              </tr>
            </thead>
            <tbody>
              {r2.files.map(f => (
                <tr key={f.name} className="border-t border-border" data-testid={`r2-row-${f.name}`}>
                  <td className="px-4 py-2 font-mono text-xs">
                    {humanDate(f.mtime)}
                    <div className="text-[11px] text-text_muted">{f.name}</div>
                  </td>
                  <td className="px-4 py-2">
                    <Badge variant="outline" className="capitalize">{f.type}</Badge>
                  </td>
                  <td className="px-4 py-2">{humanBytes(f.size)}</td>
                  <td className="px-4 py-2">
                    <span className="inline-flex items-center gap-1 text-success text-xs">
                      <CheckCircle size={13} weight="fill" /> Synchronisé
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Restore info box — SSH-only, no button */}
      <div className="bg-info-soft border border-info/40 rounded-xl p-4" data-testid="restore-info-box">
        <div className="flex items-start gap-3">
          <Info size={18} className="text-info shrink-0 mt-0.5" weight="fill" />
          <div className="flex-1 min-w-0">
            <p className="font-medium text-info mb-1">Restauration — manuelle uniquement (SSH)</p>
            <p className="text-[13px] text-text_main leading-relaxed mb-2">
              Pour des raisons de sécurité, la restauration ne peut être déclenchée que par un accès direct au serveur.
              Aucun bouton ni endpoint ne peut la lancer depuis l&apos;interface admin.
            </p>
            <pre className="text-[11px] bg-white border border-border rounded p-3 font-mono overflow-x-auto whitespace-pre">
{RESTORE_CMD}
            </pre>
            <p className="text-[11px] text-text_muted mt-2">
              Remplacez <code>NOM.gz</code> par le nom exact de l&apos;archive à restaurer.
            </p>
          </div>
        </div>
      </div>

      {/* Log tails (bottom for troubleshooting) */}
      {(status?.mongo_log_tail?.length > 0 || status?.images_log_tail?.length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="log-tails">
          {status?.mongo_log_tail?.length > 0 && (
            <div className="bg-white border border-border rounded-xl overflow-hidden">
              <p className="px-4 py-2 text-xs uppercase tracking-wide font-medium bg-surface_muted border-b border-border">
                <code>/var/log/ato-backup.log</code> (15 dernières lignes)
              </p>
              <pre className="text-[11px] font-mono p-3 overflow-x-auto whitespace-pre max-h-64">
{status.mongo_log_tail.join('\n')}
              </pre>
            </div>
          )}
          {status?.images_log_tail?.length > 0 && (
            <div className="bg-white border border-border rounded-xl overflow-hidden">
              <p className="px-4 py-2 text-xs uppercase tracking-wide font-medium bg-surface_muted border-b border-border">
                <code>/var/log/ato-images-backup.log</code> (15 dernières lignes)
              </p>
              <pre className="text-[11px] font-mono p-3 overflow-x-auto whitespace-pre max-h-64">
{status.images_log_tail.join('\n')}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
